export const CUSTOM_METRICS_PREFIX = 'CM_';

// Mirrors the backend `Aggregate` enum (`GetReportsDataAggregate`); a value missing
// here reads as summable in donutRules.ts. Pinned by metrics-aggregation-contract.node.spec.ts.
export const AGGREGATION_TYPES = Object.freeze({
  AVERAGE: 'AVERAGE',
  COUNT: 'COUNT',
  CUSTOM: 'CUSTOM',
  DATE_DIFF_AVERAGE: 'DATE_DIFF_AVERAGE',
  EARLIEST: 'EARLIEST',
  GROWTH: 'GROWTH',
  LATEST: 'LATEST',
  LATEST_VALID: 'LATEST_VALID',
  RATIO: 'RATIO',
  SUM: 'SUM',
});

export const AGGREGATION_CURRENT_GRAPH_STATS = [
  AGGREGATION_TYPES.LATEST,
  AGGREGATION_TYPES.LATEST_VALID,
];
export const AGGREGATION_ONLY_AVG_GRAPH_STATS = [AGGREGATION_TYPES.GROWTH];
export const AGGREGATION_NO_GRAPH_STATS = [AGGREGATION_TYPES.AVERAGE];

export const DURATION_FORMATS = Object.freeze({
  SHORTENED_YEARS_DAYS_HOURS_MINUTES_SECONDS: 'yy:dd:hh:mm:ss',
  HOURS_MINUTES_SECONDS: 'hh:mm:ss',
  HOURS_MINUTES: 'hh:mm',
  HOURS: 'hh',
});

export const METRIC_FORMATS = Object.freeze({
  INTEGER: '0,0',
  INTEGER_ABBREVIATED: '0a',
  FLOAT: '0.0',
  FLOAT_TWO_DECIMAL: '0.[00]a',
  FLOAT_TWO_DECIMAL_WITH_COMMA: '0,0.[00]',
  PERCENT_FLOAT_NORMAl: '0.[00]a%',
  PERCENT_FLOAT_LONG: '0.00%',
  PERCENT_FLOAT_NORMAl_WITH_COMMA: '0,0.[00]a%',
  PERCENT_FLOAT_LONG_WITH_COMMA: '0,0.00%',
  MONETARY_NORMAL: '(0,0.00a)',
  MONETARY_LONG: '(0,0.00)',
});

export const DATA_TYPES = Object.freeze({
  INTEGER: 'INTEGER',
  FLOAT: 'FLOAT',
  PERCENTAGE: 'PERCENTAGE',
  SECONDS: 'SECONDS',
  MILLISECONDS: 'MILLISECONDS',
  MONETARY: 'MONETARY',
});

export const DATA_SOURCES = Object.freeze({
  DB: 'DB',
  ES: 'ES',
  TIMESCALE_DB: 'TIMESCALE_DB',
  META_ADS_LIVE_API: 'META_ADS_LIVE_API',
  COMMUNITY: 'COMMUNITY',
  LISTENING: 'LISTENING',
});

export const FORMAT_TYPES = Object.freeze({
  NORMAL: 'NORMAL',
  LONG: 'LONG',
});

export const DATA_TYPE_FORMATS = Object.freeze({
  [DATA_TYPES.INTEGER]: {
    [FORMAT_TYPES.NORMAL]: METRIC_FORMATS.INTEGER_ABBREVIATED,
    [FORMAT_TYPES.LONG]: METRIC_FORMATS.INTEGER,
  },
  [DATA_TYPES.FLOAT]: {
    [FORMAT_TYPES.NORMAL]: METRIC_FORMATS.FLOAT_TWO_DECIMAL,
    [FORMAT_TYPES.LONG]: METRIC_FORMATS.FLOAT_TWO_DECIMAL_WITH_COMMA,
  },
  [DATA_TYPES.PERCENTAGE]: {
    [FORMAT_TYPES.NORMAL]: METRIC_FORMATS.PERCENT_FLOAT_NORMAl,
    [FORMAT_TYPES.LONG]: METRIC_FORMATS.PERCENT_FLOAT_LONG,
  },
  [DATA_TYPES.SECONDS]: {
    [FORMAT_TYPES.NORMAL]: DURATION_FORMATS.HOURS_MINUTES_SECONDS,
    [FORMAT_TYPES.LONG]: DURATION_FORMATS.HOURS_MINUTES_SECONDS,
  },
  [DATA_TYPES.MILLISECONDS]: {
    [FORMAT_TYPES.NORMAL]: DURATION_FORMATS.SHORTENED_YEARS_DAYS_HOURS_MINUTES_SECONDS,
    [FORMAT_TYPES.LONG]: DURATION_FORMATS.SHORTENED_YEARS_DAYS_HOURS_MINUTES_SECONDS,
  },
  [DATA_TYPES.MONETARY]: {
    [FORMAT_TYPES.NORMAL]: METRIC_FORMATS.MONETARY_NORMAL,
    [FORMAT_TYPES.LONG]: METRIC_FORMATS.MONETARY_LONG,
  },
});

export const POST_TYPES = Object.freeze({
  ORGANIC: 'ORGANIC',
  PROMOTED: 'PROMOTED',
  ORGANIC_PROMOTED: 'ORGANIC_PROMOTED',
  ESTIMATED_ORGANIC: 'ESTIMATED_ORGANIC',
  ADS: 'ADS',
});

export const POST_TYPE_SECTIONS = Object.freeze({
  OTHER: 'OTHER',
  ORGANIC: 'ORGANIC',
  PROMOTED: 'PROMOTED',
  ESTIMATED_ORGANIC: 'ESTIMATED_ORGANIC',
  ORGANIC_PROMOTED: 'ORGANIC_PROMOTED',
  DEPRECATED_ORGANIC: 'DEPRECATED_ORGANIC',
  DEPRECATED_ORGANIC_PROMOTED: 'DEPRECATED_ORGANIC_PROMOTED',
  ADS: 'ADS',
});

export const CONNECTION_TYPE_SECTIONS = Object.freeze({
  CONNECTED_ONLY: 'CONNECTED_ONLY',
  PUBLIC_DATA: 'PUBLIC_DATA',
});

const POST_SOURCE_SECTIONS = Object.freeze({
  PUBLIC: 'PUBLIC',
});

const METRIC_CATEGORY_SECTIONS = Object.freeze({
  ACCOUNT: 'ACCOUNT',
  LIKESHOP: 'LIKESHOP',
});

export const METRIC_CATEGORY_SECTIONS_TITLE = Object.freeze({
  ACCOUNT: 'Account',
  LIKESHOP: 'LikeShop',
});

export const CONTENT_METRIC_SECTIONS = Object.freeze({
  OTHER: POST_TYPE_SECTIONS.OTHER,
  ACCOUNT: METRIC_CATEGORY_SECTIONS.ACCOUNT,
  ORGANIC: POST_TYPE_SECTIONS.ORGANIC,
  ESTIMATED_ORGANIC: POST_TYPE_SECTIONS.ESTIMATED_ORGANIC,
  PROMOTED: POST_TYPE_SECTIONS.PROMOTED,
  ORGANIC_PROMOTED: POST_TYPE_SECTIONS.ORGANIC_PROMOTED,
  LIKESHOP: METRIC_CATEGORY_SECTIONS.LIKESHOP,
  DEPRECATED_ORGANIC: POST_TYPE_SECTIONS.DEPRECATED_ORGANIC,
  DEPRECATED_ORGANIC_PROMOTED: POST_TYPE_SECTIONS.DEPRECATED_ORGANIC_PROMOTED,
  ADS: POST_TYPE_SECTIONS.ADS,
  ...CONNECTION_TYPE_SECTIONS,
  ...POST_SOURCE_SECTIONS,
});

export const POST_TYPE_SECTION_TITLE = {
  ORGANIC: 'Organic',
  PROMOTED: 'Promoted',
  ESTIMATED_ORGANIC: 'Est. Organic',
  ORGANIC_PROMOTED: 'Organic + Promoted',
  DEPRECATED_ORGANIC: 'Discontinued - Organic',
  DEPRECATED_ORGANIC_PROMOTED: 'Discontinued - Organic + Promoted',
  PUBLIC: 'Public',
};

const CONNECTION_TYPE_SECTION_TITLE = {
  CONNECTED_ONLY: 'Connected Creators Only',
  PUBLIC_DATA: 'Public Data',
};

export const CONTENT_METRIC_SECTION_TOOLTIPS = Object.freeze({
  ORGANIC: 'The changes to these metrics are strictly the result of organic activity',
  PROMOTED: 'The changes to these metrics are strictly the result of promoted initiatives',
  ESTIMATED_ORGANIC: 'The changes to these metrics are estimated based on organic activity',
  ORGANIC_PROMOTED:
    'The changes to these metrics can be the result of organic activity, promoted initiatives, or a combination of both',
  DEPRECATED_ORGANIC:
    'These metrics are no longer supported. They were strictly the result of organic activity',
  DEPRECATED_ORGANIC_PROMOTED:
    'These metrics are no longer supported and could be the result of organic activity, promoted initiatives, or a combination of both',
  ACCOUNT: "These are account stats and don't directly relate to posts",
  LIKESHOP: 'These metrics relate to your LikeShop performance',
});

export const CONTENT_METRIC_SECTION_TITLES = Object.freeze({
  ...POST_TYPE_SECTION_TITLE,
  ...CONNECTION_TYPE_SECTION_TITLE,
  ...METRIC_CATEGORY_SECTIONS_TITLE,
});

export const QUERY_TIMEFRAME = Object.freeze({
  ACTIVITY_DURING_TIMEFRAME: 'ACTIVITY_DURING_TIMEFRAME',
  POSTS_PUBLISHED_IN_TIMEFRAME: 'POSTS_PUBLISHED_IN_TIMEFRAME',
});

export const DASHING_UI_TIMEFRAME_MAP = Object.freeze({
  ACTIVITY_DURING_TIMEFRAME: {
    text: 'Activity This Period',
    value: 'ACTIVITY_THIS_PERIOD',
  },
  POSTS_PUBLISHED_IN_TIMEFRAME: {
    text: 'Posts This Period',
    value: 'POSTS_THIS_PERIOD',
  },
});

export const PUBLIC_DATA = Object.freeze({
  value: 'PUBLIC_DATA',
  label: 'Public Data',
  tooltip: 'Public Data can contain data from both unconnected creators and connected creators',
  banner: false,
});

export const CONNECTED_CREATORS_ONLY = Object.freeze({
  value: 'CONNECTED_CREATORS_ONLY',
  label: 'Connected Creators Only',
  banner: true,
});

export const METRIC_CONNECTION_STATUS_OPTIONS = Object.freeze({
  [PUBLIC_DATA.value]: PUBLIC_DATA,
  [CONNECTED_CREATORS_ONLY.value]: CONNECTED_CREATORS_ONLY,
});

export const DASHING_UI_POST_TYPE_MAP = Object.freeze({
  ORGANIC: {
    text: 'Organic',
    value: 'organic',
  },
  PROMOTED: {
    text: 'Promoted',
    value: 'promoted',
  },
  ORGANIC_PROMOTED: {
    text: 'Organic + Promoted',
    value: 'organic-promoted',
  },
  ADS: {
    text: 'Ads',
    value: 'ads',
  },
});

export const TOTAL_TSI_METRIC = 'TOTAL_TSI';
export const ONLINE_FOLLOWERS_METRIC = 'ONLINE_FOLLOWERS';
